<!-- Loading State -->
@if (isLoading) {
  <div class="cyber-loading-state">
    <div class="cyber-spinner">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <p class="cyber-loading-text">Loading available plans...</p>
  </div>
}

<!-- Channel-partner company (#943): no prices, no checkout. -->
@if (!isLoading && isPartnerCompany) {
  <app-partner-plan-note class="d-block mb-4" heading="Your plan" subheading="Subscription and billing"></app-partner-plan-note>
}

@if (!isPartnerCompany) {
  <!-- Current Plan Status (Company-based mode) -->
  @if (!isLoading && showCompanyBased && !!company) {
    <div class="cyber-alert cyber-alert-info mb-4">
      <div class="d-flex align-items-start">
        <i class="bi bi-info-circle-fill flex-shrink-0 me-3"></i>
        <div class="flex-grow-1">
          <h6 class="cyber-alert-heading mb-1">Your current plan</h6>
          <p class="mb-0">
            <span class="text-capitalize fw-semibold">{{ company.tier === "free" ? "No active plan" : company.tier }}</span>
            @if (company.tier === "trial") {
              <span class="ms-2">
                · {{ getTrialDaysRemaining(company) }} days remaining
              </span>
            }
            @if (company.tier === "free") {
              <!-- Continue to dashboard -->
              <div class="action-btn mt-2">
                <button
                  type="button"
                  class="btn btn-primary btn-gradient btn-cyber-plan btn-cyber-contact"
                  (click)="navigateToDashboard()">
                  Continue to dashboard
                </button>
              </div>
            }
          </p>
        </div>
      </div>
    </div>
  }

  <!-- Plan Cards -->
  @if (!isLoading && plans && plans.length > 0) {
    <app-plan-steps class="d-block mb-4 sn-plan-steps-center" [current]="planStep" [done]="planStepsDone" (stepSelect)="goToStep($event)"></app-plan-steps>
    <p class="visually-hidden" aria-live="polite">{{ selectionAnnouncement }}</p>
    @if (advertisePricing) {
      <div class="d-flex justify-content-center mb-4">
        <div class="sn-billing-toggle-pill" role="group" aria-label="Billing interval">
          <button
            type="button"
            class="sn-toggle-option"
            [class.active]="billingInterval === 'annual'"
            (click)="setBillingInterval('annual')"
            [attr.aria-pressed]="billingInterval === 'annual'"
            >
            Annual
            <span class="sn-savings-badge" aria-label="Two months free on annual billing">2 months free</span>
          </button>
          <button
            type="button"
            class="sn-toggle-option"
            [class.active]="billingInterval === 'monthly'"
            (click)="setBillingInterval('monthly')"
            [attr.aria-pressed]="billingInterval === 'monthly'"
            [disabled]="!!selectedPlanId && !isBillingIntervalAllowedForSelectedPlan('monthly')"
            >
            Monthly
          </button>
        </div>
      </div>
    }

    @if (advertisePricing && promoCodesEnabled) {
      <app-promo-code-box
        class="d-block mb-4"
        inputId="sn-promo-input"
        [code]="promoCodeInput"
        [applied]="appliedPromo"
        [error]="promoError"
        [errorKind]="promoErrorKind"
        [checking]="promoChecking"
        (codeChange)="promoCodeInput = $event"
        (apply)="applyPromoCode()"
        (remove)="clearPromoCode()"
      ></app-promo-code-box>
    }

    <div class="cyber-plans-grid">
      @for (plan of plans; track plan; let i = $index) {
        <div class="cyber-plan-card" [class.featured]="!!plan.isFeatured" [class.is-staged]="isStagedPlan(plan.uid)">
          <!-- Featured Badge -->
          @if (!!plan.isFeatured) {
            <div class="cyber-featured-badge">
              <i class="bi bi-star-fill"></i>
              <span>Popular</span>
            </div>
          }

          <!-- Plan Icon -->
          <div class="cyber-plan-icon">
            @if (plan.icon) {
              <img title="{{ plan.name }}" [src]="plan.icon" [alt]="plan.name" class="cyber-plan-image" />
            } @else {
              <i class="bi bi-shield-check cyber-plan-icon-fallback" aria-hidden="true"></i>
            }
          </div>

          <!-- Plan Name -->
          <h3 class="cyber-plan-name">{{ plan.name }}</h3>

          <!-- Plan Pricing -->
          @if (plan.isFree) {
            <div class="cyber-plan-price">
              <span class="cyber-price-current">Free</span>
            </div>
          } @else if (advertisePricing && !!plan.hasPrice) {
            <div class="cyber-plan-price">
              @if (isPlanDiscounted(plan)) {
                <span class="cyber-price-was" aria-label="List price">
                  {{ centsToDollars(getPlanListPriceCents(plan)) | currency }}
                </span>
              }
              <span class="cyber-price-current">
                {{ centsToDollars(getPlanPriceCents(plan)) | currency }}
              </span>
              <span class="cyber-price-period">{{ getBillingLabel() }}</span>
              @if (isPlanDiscounted(plan)) {
                <span class="cyber-price-promo-note">
                  {{ appliedPromo?.description }}
                </span>
              }
            </div>
          } @else {
            <div class="cyber-plan-price">
              <span class="cyber-price-custom">Contact us for a quote</span>
            </div>
          }

          <!-- Plan Description -->
          @if (plan.description) {
            <p class="cyber-plan-description" [innerHTML]="plan.description"></p>
          }

          <!-- Features Section -->
          <div class="cyber-features-section">
            <div class="cyber-features-header">
              @if (i === 0) {
                <span>Features</span>
              } @else {
                <span>All {{ plans[i - 1].name }} features, plus:</span>
              }
            </div>

            <div class="cyber-features-list" role="list">
              @for (feature of getUniqueFeatures(i); track feature) {
                <div role="listitem" class="cyber-feature-item" [class.disabled]="feature.type === 'boolean' && !feature.value">
                  <i class="bi"
                  [ngClass]="feature.type === 'boolean' ? (feature.value ? 'bi-check-circle-fill cyber-feature-icon' : 'bi-x-circle cyber-feature-icon-disabled') : 'bi-check-circle-fill cyber-feature-icon'"></i>
                  <span class="cyber-feature-text">
                    @if (feature.type === 'number') {
                      <strong>{{ feature.value === 0 ? 'Unlimited' : feature.value }}</strong> {{ feature.name }}
                    } @else {
                      {{ feature.name }}
                    }
                  </span>
                </div>
              }
            </div>
          </div>

          <!-- Coins Credits Row -->
          <div class="sn-plan-credits-row">
            <div class="sn-credits-allotment">
              <i class="fas fa-coins text-warning me-1" aria-hidden="true"></i>
              @if ((plan.monthlyCredits ?? 0) > 0) {
                <span><strong>{{ plan.monthlyCredits | number }}</strong> Coins / mo</span>
              } @else {
                <span class="text-muted">No coins included</span>
              }
            </div>
          </div>

          <!-- Action Button -->
          <div class="cyber-plan-action">
            @if ((advertisePricing || plan.isFree) && !!plan.hasPrice) {
              <button type="button" (click)="selectPlan(plan.uid)" [ngClass]="getPlanButtonClass(plan.uid)" [disabled]="isPlanButtonDisabled(plan.uid)" class="btn-cyber-plan"
                [attr.aria-pressed]="isStagedPlan(plan.uid)">
                <span>{{ getPlanButtonText(plan.uid) }}</span>
                @if (isCurrentPlan(plan.uid)) {
                  <i class="bi bi-check-circle-fill ms-2" aria-hidden="true"></i>
                }
              </button>
              @if (isStagedPlan(plan.uid)) {
                <p class="sn-plan-selected-hint">Plan selected. Finish checkout below to activate it.</p>
              }
            } @else {
              <a routerLink="/dashboard/support/create" class="btn-cyber-plan btn-cyber-contact">
                <span>Contact us</span>
                <i class="bi bi-arrow-right ms-2"></i>
              </a>
            }
          </div>
        </div>
      }
    </div>

    <!-- Pricing Note -->
    <div class="cyber-pricing-note">
      <i class="bi bi-info-circle me-2"></i>
      @if (advertisePricing) {
        <span>Some pricing limitations may apply based on device count</span>
      } @else {
        <span>Our team scopes every plan to your environment &mdash; contact us for a quote</span>
      }
    </div>
  }

  <!-- Coin Packs (only show when plan is selected) -->
  @if (selectedPlanId && tokenPacks.length > 0) {
    <div class="cyber-addons-section">
      <div class="cyber-addons-card">
        <div class="cyber-addons-header">
          <div class="cyber-addons-icon">
            <i class="fas fa-coins"></i>
          </div>
          <h4 class="cyber-addons-title">Need more Coins?</h4>
          <p class="cyber-addons-subtitle">
            Top up any time. Pack Coins arrive monthly on top of your allotment and expire at the end of each pack period.
          </p>
        </div>

        <div class="cyber-addons-grid">
          @for (pack of tokenPacks; track pack) {
            <button type="button" class="cyber-addon-item" [class.selected]="isAddonSelected(pack.id)"
              (click)="toggleAddon(pack.id)"
              [attr.aria-pressed]="isAddonSelected(pack.id) ? 'true' : 'false'">
              <i class="bi" [class.bi-check-circle-fill]="isAddonSelected(pack.id)" [class.bi-plus-circle]="!isAddonSelected(pack.id)" class="cyber-addon-icon"></i>
              <div class="cyber-addon-content">
                <span class="cyber-addon-text">{{ pack.name }}</span>
                @if (pack.description) {
                  <span class="cyber-addon-description">{{ pack.description }}</span>
                }
              </div>
              @if (advertisePricing) {
                <span class="cyber-addon-price">
                  {{ centsToDollars(getAddOnBillablePriceCents(pack)) | currency }}
                  {{ getBillingLabel() }}
                </span>
              } @else {
                <span class="cyber-addon-price contact-sales-badge">Contact us</span>
              }
            </button>
          }
        </div>

        <p class="cyber-addons-footer">
          Compliance programs and platform modules are scoped with our team &mdash;
          <a routerLink="/dashboard/support/create">talk to us</a> about adding one.
        </p>
      </div>
    </div>
  }

  <!-- What Coins are spent on -->
  <div class="sn-token-usage-section">
    <button
      type="button"
      class="sn-token-usage-toggle"
      (click)="toggleTokenUsage()"
      [attr.aria-expanded]="showTokenUsage"
      aria-controls="sn-token-usage-panel">
      <i class="fas fa-coins text-warning me-2" aria-hidden="true"></i>
      <span class="sn-token-usage-title">What uses Coins?</span>
      <span class="sn-token-usage-hint">
        Scheduled scans and the dashboard never cost coins
      </span>
      <i class="bi ms-auto" [class.bi-chevron-down]="!showTokenUsage" [class.bi-chevron-up]="showTokenUsage" aria-hidden="true"></i>
    </button>

    @if (showTokenUsage) {
      <div id="sn-token-usage-panel" class="sn-token-usage-panel">
        <p class="sn-token-usage-intro">
          Your plan includes a monthly Coin allotment. Coins are spent only when
          you ask the platform to do AI work on demand &mdash; everything that runs on a
          schedule, and everything you simply read, is included.
        </p>

        <div class="sn-token-usage-columns">
          <div class="sn-token-usage-metered">
            <h6 class="sn-token-usage-heading">Costs coins</h6>
            <table class="sn-token-usage-table">
              <caption class="visually-hidden">Coin cost per action</caption>
              <thead>
                <tr>
                  <th scope="col">Action</th>
                  <th scope="col" class="text-end">Coins</th>
                </tr>
              </thead>
              <tbody>
                @for (item of tokenUsageMetered; track item.action) {
                  <tr>
                    <td>
                      <span class="sn-token-action">{{ item.action }}</span>
                      <span class="sn-token-note">{{ item.note }}</span>
                    </td>
                    <td class="text-end sn-token-cost">{{ item.cost | number }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="sn-token-usage-included">
            <h6 class="sn-token-usage-heading">Always included</h6>
            <ul class="sn-token-usage-list">
              @for (item of tokenUsageIncluded; track item) {
                <li><i class="bi bi-check-circle-fill" aria-hidden="true"></i>{{ item }}</li>
              }
            </ul>
            <p class="sn-token-usage-footnote">
              If a job fails before it produces anything, it costs nothing. Run out of
              coins and on-demand AI pauses &mdash; your scheduled scans, findings and
              dashboards keep working.
            </p>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- On the roadmap -->
  <div class="sn-roadmap-section">
    <div class="sn-roadmap-header">
      <h4 class="sn-roadmap-title">On the roadmap</h4>
      <p class="sn-roadmap-subtitle">
        What we are building next. These are not included in any plan yet.
      </p>
    </div>

    <div class="sn-roadmap-grid">
      @for (item of roadmapItems; track item.id) {
        <div class="sn-roadmap-item">
          <i class="bi sn-roadmap-icon" [ngClass]="item.icon" aria-hidden="true"></i>
          <div class="sn-roadmap-content">
            <span class="sn-roadmap-item-title">{{ item.title }}</span>
            <span class="sn-roadmap-item-description">{{ item.description }}</span>
            <span class="sn-roadmap-plan">Planned for {{ item.plan }} and above</span>
          </div>
        </div>
      }
    </div>
  </div>
}

<!-- Sticky checkout bar (#956): the existing confirm handler, nothing new. -->
<app-checkout-bar [summary]="checkoutSummary" [busy]="checkoutBusy" (primary)="onCheckoutPrimary()" (clear)="clearSelection()"></app-checkout-bar>
